const META_PIXEL_ID = '3721962801299577'
const SLUG_LINK_PESSOAL = 'monica'
const CONSENT_KEY = 'dso_consentimento_pixel_monica'
const PAGEVIEW_KEY = 'meta_pixel_pageviews_monica'
const LEAD_KEY = 'meta_pixel_lead_monica'
const LEAD_PENDENTE_KEY = 'meta_pixel_lead_pendente_monica'

const CAPI_FUNCTION_URL = 'https://api.lumi.new/v1/functions/p427216140118241280/meta-capi'

type FbqFn = ((...args: unknown[]) => void) & {
  callMethod?: (...args: unknown[]) => void
  queue: unknown[][]
  loaded?: boolean
  version?: string
  push?: FbqFn
}

declare global {
  interface Window {
    fbq?: FbqFn
    _fbq?: FbqFn
  }
}

export type ConsentimentoPixel = 'aceito' | 'recusado' | null

let pixelInicializado = false

export function ehLinkPessoalMonica(slug?: string | null): boolean {
  return String(slug || '').toLowerCase().trim() === SLUG_LINK_PESSOAL
}

export function lerConsentimentoPixel(): ConsentimentoPixel {
  if (typeof window === 'undefined') return null
  try {
    const valor = window.localStorage.getItem(CONSENT_KEY)
    if (valor === 'aceito' || valor === 'recusado') return valor
    return null
  } catch {
    return null
  }
}

export function consentimentoPixelAceito(): boolean {
  return lerConsentimentoPixel() === 'aceito'
}

function salvarConsentimentoPixel(valor: 'aceito' | 'recusado'): void {
  try {
    window.localStorage.setItem(CONSENT_KEY, valor)
  } catch {
    // localStorage pode estar indisponivel
  }
}

function garantirPixel(): boolean {
  if (typeof window === 'undefined') return false
  if (!consentimentoPixelAceito()) return false

  if (window.fbq) {
    pixelInicializado = true
    return true
  }

  const fbq: FbqFn = function (...args: unknown[]) {
    if (fbq.callMethod) {
      fbq.callMethod(...args)
    } else {
      fbq.queue.push(args)
    }
  } as FbqFn
  fbq.queue = []
  fbq.loaded = true
  fbq.version = '2.0'
  fbq.push = fbq

  window.fbq = fbq
  window._fbq = fbq

  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  const firstScript = document.getElementsByTagName('script')[0]
  firstScript?.parentNode?.insertBefore(script, firstScript)

  window.fbq('init', META_PIXEL_ID)
  pixelInicializado = true
  return true
}

function lerChaves(chave: string): string[] {
  try {
    const bruto = sessionStorage.getItem(chave)
    if (!bruto) return []
    const parsed = JSON.parse(bruto)
    return Array.isArray(parsed) ? parsed.filter((item) => typeof item === 'string') : []
  } catch {
    return []
  }
}

function gravarChaves(chave: string, valores: string[]): void {
  try {
    sessionStorage.setItem(chave, JSON.stringify(valores))
  } catch {
    // sessionStorage pode estar indisponivel
  }
}

function gerarEventId(): string {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID()
    }
  } catch {
    // segue para o fallback
  }
  return `evt_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`
}

function lerCookie(nome: string): string | undefined {
  try {
    const alvo = `${nome}=`
    const partes = document.cookie.split(';')
    for (const parte of partes) {
      const item = parte.trim()
      if (item.startsWith(alvo)) {
        return decodeURIComponent(item.substring(alvo.length))
      }
    }
    return undefined
  } catch {
    return undefined
  }
}

/**
 * Envia o mesmo evento (PageView/Lead) para a Conversions API do lado do servidor,
 * usando o mesmo event_id do Pixel para que o Meta deduplique. Não envia nome, e-mail,
 * telefone, respostas do diagnóstico ou qualquer dado de saúde — apenas o necessário
 * para o disparo (event_id, URL, e opcionalmente fbp/fbc para qualidade de correspondência).
 */
function enviarEventoServidor(eventName: 'PageView' | 'Lead', eventId: string): void {
  try {
    const payload = {
      event_name: eventName,
      event_id: eventId,
      event_source_url: window.location.href,
      fbp: lerCookie('_fbp'),
      fbc: lerCookie('_fbc'),
    }
    fetch(CAPI_FUNCTION_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    }).catch(() => {
      // Falha silenciosa: a Conversions API é um complemento, o Pixel do navegador já disparou
    })
  } catch {
    // Sem impacto na experiência do usuário caso o envio falhe
  }
}

export function dispararMetaPageView(slug?: string | null, pagina?: string): void {
  if (!ehLinkPessoalMonica(slug)) return
  if (!consentimentoPixelAceito()) return
  if (!garantirPixel()) return

  const chave = pagina || window.location.pathname
  const jaDisparados = lerChaves(PAGEVIEW_KEY)
  if (jaDisparados.includes(chave)) return
  gravarChaves(PAGEVIEW_KEY, [...jaDisparados, chave])

  const eventId = gerarEventId()
  window.fbq?.('track', 'PageView', {}, { eventID: eventId })
  enviarEventoServidor('PageView', eventId)
}

export function dispararMetaLead(slug?: string | null): void {
  if (!ehLinkPessoalMonica(slug)) return
  if (lerConsentimentoPixel() === 'recusado') return
  if (lerChaves(LEAD_KEY).includes('lead')) return

  if (!consentimentoPixelAceito()) {
    gravarChaves(LEAD_PENDENTE_KEY, ['lead'])
    return
  }

  if (!garantirPixel()) return
  gravarChaves(LEAD_KEY, ['lead'])

  const eventId = gerarEventId()
  window.fbq?.('track', 'Lead', {}, { eventID: eventId })
  enviarEventoServidor('Lead', eventId)
}

export function aceitarPixelMonica(slug?: string | null, pagina?: string): void {
  if (!ehLinkPessoalMonica(slug)) return
  salvarConsentimentoPixel('aceito')
  dispararMetaPageView(slug, pagina)
  if (lerChaves(LEAD_PENDENTE_KEY).includes('lead')) {
    dispararMetaLead(slug)
  }
}

export function recusarPixelMonica(): void {
  salvarConsentimentoPixel('recusado')
}

export function pixelMonicaAtivo(): boolean {
  return pixelInicializado
}
